import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { useAuthStore } from '../stores/authStore';

/**
 * Role-aware variant of `useAuthGuard`. Redirects:
 *   - to `/login?return=<path>` when the user is signed out
 *   - to `/` (with `?role-denied=1`) when the user is signed in but
 *     does NOT carry one of the allowed roles
 *
 * Returns `{ isAuthorized }` so callers can render `null` while the
 * redirect is in flight (mirrors useAuthGuard's shape so call sites
 * read the same way).
 */
export function useRoleGuard(...allowedRoles: string[]): { isAuthorized: boolean } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.claims?.role);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthorized = isAuthenticated && Boolean(role) && allowedRoles.includes(role as string);

  useEffect(() => {
    if (!isAuthenticated) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnPath)}`, { replace: true });
      return;
    }
    if (!allowedRoles.includes(role as string)) {
      navigate('/?role-denied=1', { replace: true });
    }
    // Roles are stable per session; only re-evaluate on auth changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, role, location.pathname, location.search]);

  return { isAuthorized };
}

export default useRoleGuard;
